import { useEffect, useMemo, useRef, useState } from 'react'
import { DEFAULT_PROGRESSION as P } from '../../rules/progression/config'
import { Glove } from '../v2/hex/GestureHand'
import InvCard, { LotArt, StructureArt, ResourceArt, GemArt, capitalize, lotName } from './InvCard'
import './ChestReveal.css'

// ─── Opening a chest ─────────────────────────────────────────────────────────
// Every chest, the Genesis Chest after the tutorial and each War Chest after
// a war, opens here. The player taps the chest TAPS times: each tap grows it
// and fills one of the dots under it. On the last tap its lid flies up and
// what it held comes out as inventory cards (InvCard), then the button out.
// `receipt` may arrive late (the Genesis Chest opens on the server): a chest
// tapped open before it has wobbles until it comes.
// The first TEACH_CHESTS chests on this device show a glove tapping it.
// A structure the player already owns (or a lot the Hive has no room for)
// comes out as its card, charges up and bursts into the resources it's paid
// out in, counting up (ConvertGroup).

const TAPS = 4
const LID_MS = 500
const CARD_STAGGER_MS = 140
const CARD_IN_MS = 450
const TEACH_CHESTS = 2
// A conversion, after its card lands: the charge-up, then the burst.
const CHARGE_AT_MS = 350
const BURST_AT_MS = CHARGE_AT_MS + 650
const COUNT_MS = 700
const SEEN_KEY = 'hog.chestsOpened'

const fmt = (n) => Math.floor(n ?? 0).toLocaleString('en-US')

// How many chests this device has opened (a per-viewer nicety: storage may
// be unavailable, and then every chest teaches).
function chestsSeen() {
    try { return Number(window.localStorage.getItem(SEEN_KEY)) || 0 } catch { return 0 }
}
function noteChestOpened() {
    try { window.localStorage.setItem(SEEN_KEY, String(chestsSeen() + 1)) } catch { /* not stored */ }
}

// What a duplicate structure or an overflowed lot was paid out in (the
// receipt's raw already includes it), as [resource, amount] pairs.
const dupPayout = (name) => Object.entries(P.structures[name]?.duplicatePayout ?? {}).filter(([, n]) => n > 0)
const overflowPayout = (type) => [[type, P.inputs.lotOverflowRaw]]

// The receipt as cards, in the order they come out (also the war end's list
// of opened chests). A conversion is one item: { convert: true, art, name,
// sub, payout }. The plain resource cards are what's left of the receipt's
// raw once the conversions' payouts are taken out.
export function contentsOf(receipt) {
    const out = []
    const raw = { ...(receipt.raw ?? {}) }
    const takeOut = (pairs) => { for (const [r, n] of pairs) raw[r] = (raw[r] ?? 0) - n }
    for (const [type, n] of Object.entries(receipt.lots ?? {})) {
        for (let k = 0; k < n; k++) out.push({ key: `lot-${type}-${k}`, art: <LotArt type={type} />, name: lotName(type), sub: 'Lot' })
    }
    for (const name of receipt.structures ?? []) out.push({ key: `new-${name}`, art: <StructureArt name={name} />, name, sub: 'New' })
    for (const [k, name] of (receipt.duplicates ?? []).entries()) {
        const payout = dupPayout(name)
        takeOut(payout)
        out.push({ key: `dup-${name}-${k}`, convert: true, art: <StructureArt name={name} />, name, sub: 'Already owned', payout })
    }
    for (const [k, type] of (receipt.overflow ?? []).entries()) {
        const payout = overflowPayout(type)
        takeOut(payout)
        out.push({ key: `over-${type}-${k}`, convert: true, art: <LotArt type={type} />, name: `${lotName(type)} lot`, sub: 'Hive full', payout })
    }
    for (const [r, n] of Object.entries(raw)) {
        if (n > 0) out.push({ key: `raw-${r}`, art: <ResourceArt type={r} />, name: capitalize(r), sub: `+${fmt(n)}` })
    }
    if (receipt.gems > 0) out.push({ key: 'gems', art: <GemArt />, name: 'Gems', sub: `+${fmt(receipt.gems)}` })
    return out
}

// Counts 0 → `to` over COUNT_MS once `run` turns true.
function useCountUp(to, run) {
    const [n, setN] = useState(0)
    const raf = useRef(null)
    useEffect(() => {
        if (!run) return undefined
        const t0 = performance.now()
        const tick = (t) => {
            const k = Math.min(1, (t - t0) / COUNT_MS)
            setN(Math.round(to * (1 - (1 - k) ** 3)))
            if (k < 1) raf.current = requestAnimationFrame(tick)
        }
        raf.current = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(raf.current)
    }, [to, run])
    return n
}

function PayoutCard({ r, n, i, run }) {
    const shown = useCountUp(n, run)
    return <InvCard className="cr__card--payout" style={{ '--j': i }} art={<ResourceArt type={r} />} name={capitalize(r)} sub={`+${fmt(shown)}`} />
}

// A conversion: the card lands (after `delay`), charges, then bursts into its
// payout cards.
function ConvertGroup({ it, i, delay }) {
    const [stage, setStage] = useState('in')     // in → charge → burst
    useEffect(() => {
        const a = setTimeout(() => setStage('charge'), delay + CARD_IN_MS + CHARGE_AT_MS)
        const b = setTimeout(() => setStage('burst'), delay + CARD_IN_MS + BURST_AT_MS)
        return () => { clearTimeout(a); clearTimeout(b) }
    }, [delay])
    return (
        <div className="cr__convert" data-stage={stage}>
            {stage !== 'burst' ? (
                <InvCard className={`cr__card${stage === 'charge' ? ' cr__card--charging' : ''}`} style={{ '--i': i }} art={it.art} name={it.name} sub={it.sub} />
            ) : (
                <>
                    <span className="cr__burst" aria-hidden="true" />
                    <span className="cr__convert-note">{it.name} → resources</span>
                    {it.payout.map(([r, n], j) => <PayoutCard key={r} r={r} n={n} i={j} run />)}
                </>
            )}
        </div>
    )
}

export default function ChestReveal({ receipt, onDone, doneLabel = 'Done' }) {
    const [teach] = useState(() => chestsSeen() < TEACH_CHESTS)
    const [taps, setTaps] = useState(0)
    // tapping → open (lid up) → contents (cards out)
    const [phase, setPhase] = useState('tapping')
    const [landed, setLanded] = useState(false)

    useEffect(() => {
        if (phase === 'tapping' && taps >= TAPS && receipt) {
            setPhase('open')
            noteChestOpened()
        }
    }, [phase, taps, receipt])
    useEffect(() => {
        if (phase !== 'open') return undefined
        const t = setTimeout(() => setPhase('contents'), LID_MS)
        return () => clearTimeout(t)
    }, [phase])

    const items = useMemo(() => (phase === 'contents' ? contentsOf(receipt) : []), [phase, receipt])
    useEffect(() => {
        if (phase !== 'contents') return undefined
        const last = items.reduce((m, it, i) => Math.max(m, i * CARD_STAGGER_MS + CARD_IN_MS + (it.convert ? BURST_AT_MS + COUNT_MS : 0)), 0)
        const t = setTimeout(() => setLanded(true), last)
        return () => clearTimeout(t)
    }, [phase, items])

    const tap = () => { if (taps < TAPS) setTaps(n => n + 1) }
    const shut = phase === 'tapping'
    // Bigger with every tap.
    const scale = 1 + Math.min(taps, TAPS) * 0.12

    return (
        <div className="cr" data-testid="chest-reveal" data-phase={phase}>
            <div className="cr__stage">
                <button className={`cr__chest${shut ? '' : ' cr__chest--open'}${shut && taps >= TAPS ? ' cr__chest--waiting' : ''}`}
                    style={{ '--scale': scale }} onClick={tap} disabled={!shut} aria-label={`Tap the chest (${taps} of ${TAPS})`}>
                    <div className="cr__glow" />
                    {/* A thump on every tap: keyed so each one replays. */}
                    <div key={taps} className={taps > 0 && shut ? 'cr__thump' : undefined}>
                        <svg viewBox="0 0 120 100" className="cr__svg" aria-hidden="true">
                            <g className="cr__lid">
                                <path d="M10 44 Q10 18 60 18 Q110 18 110 44 Z" fill="#8a5a2b" stroke="#2a1a0c" strokeWidth="3" />
                                <rect x="52" y="18" width="16" height="26" fill="#e0b040" stroke="#2a1a0c" strokeWidth="2" />
                            </g>
                            <rect x="10" y="44" width="100" height="48" rx="3" fill="#6e4520" stroke="#2a1a0c" strokeWidth="3" />
                            <rect x="52" y="44" width="16" height="48" fill="#e0b040" stroke="#2a1a0c" strokeWidth="2" />
                            <rect x="54" y="52" width="12" height="12" rx="2" fill="#2a1a0c" />
                        </svg>
                    </div>
                    {teach && shut && taps < TAPS && (
                        <span className="cr__hand" aria-hidden="true">
                            <span className="cr__hand-ripple" />
                            <span className="cr__hand-glove"><Glove /></span>
                        </span>
                    )}
                </button>
                {shut && (
                    <div className="cr__dots" aria-hidden="true">
                        {Array.from({ length: TAPS }, (_, i) => <span key={i} className={i < taps ? 'cr__dot cr__dot--on' : 'cr__dot'} />)}
                    </div>
                )}
            </div>

            {items.length > 0 && (
                <div className="cr__cards">
                    {items.map((it, i) => it.convert
                        ? <ConvertGroup key={it.key} it={it} i={i} delay={i * CARD_STAGGER_MS} />
                        : <InvCard key={it.key} className="cr__card" style={{ '--i': i }} art={it.art} name={it.name} sub={it.sub} />)}
                </div>
            )}

            <div className="cr__foot">
                {landed && <button className="cr__cta" onClick={onDone}>{doneLabel}</button>}
            </div>
        </div>
    )
}
